/** Typed API client for Chrona backend (/api/v1). */

import { API_BASE } from '@/design/tokens';
import { Telemetry } from './telemetry';
import { createLogger } from './debug';
import { isOrgSwitchRejectionMessage, isSsoRequiredMessage } from './orgAccess';

const log = createLogger('api');
import type {
  HealthResponse,
  Project,
  FolderFilterState,
  FolderFilterDefaults,
  Submission,
  Document,
  IngestTaskResponse,
  AnalyzeRequest,
  AnalyzeResponse,
  ResolveLatestResponse,
  ManifestStatus,
  ExplorerResponse,
  DocumentInfo,
  ScanResponse,
  UnclassifiedFile,
  DuplicateGroupOut,
  AppConfig,
  ModelOption,
  AbbreviationScanResponse,
  CompoundCodeScanResponse,
  CrossReferenceScanResponse,
  SponsorConsistencyScanResponse,
  StudyProfileResponse,
  SubstanceProfileResponse,
  DoseUnitScanResponse,
  SectionOwnerScanResponse,
  TaskStatus,
  TaskStartResponse,
  AuditEvent,
  ReadinessSnapshot,
  SetupStatusResponse,
  AuthUser,
  OrgMember,
  InviteResponse,
  CheckDomainResponse,
  SubmissionTypeOption,
  SubmissionClassification,
  ConsistencyResponse,
  AccountResponse,
} from '@/types/api';
import { supabase } from './supabaseClient';

const IS_LOCAL_DEV = import.meta.env.VITE_LOCAL_DEV === 'true';

// --- Token cache — avoids Web Lock contention during Supabase token refresh ---
// onAuthStateChange delivers new tokens without acquiring the Web Lock,
// so we cache the token here and skip getSession() during task polling.
let _cachedToken: string | null = null;
let _tokenExpiresAt = 0; // epoch seconds

if (!IS_LOCAL_DEV) {
  supabase.auth.onAuthStateChange((_event, session) => {
    if (session?.access_token) {
      _cachedToken = session.access_token;
      _tokenExpiresAt = session.expires_at ?? 0;
      log.log('token cache updated, expires', new Date(_tokenExpiresAt * 1000).toISOString());
    } else {
      _cachedToken = null;
      _tokenExpiresAt = 0;
      log.log('token cache cleared');
    }
  });
}

/** Convert snake_case keys to camelCase recursively. */
function snakeToCamel(str: string): string {
  return str.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
}

function convertKeys(obj: unknown): unknown {
  if (Array.isArray(obj)) {
    return obj.map(convertKeys);
  }
  if (obj !== null && typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj as Record<string, unknown>).map(([key, value]) => [
        snakeToCamel(key),
        convertKeys(value),
      ])
    );
  }
  return obj;
}

/** localStorage key for the selected active org (A2F-244). */
export const ACTIVE_ORG_STORAGE_KEY = 'chrona.active_org_id';

/** Read active org id from localStorage, if any. Keeps api.ts framework-free. */
function readActiveOrgId(): string | null {
  try {
    return localStorage.getItem(ACTIVE_ORG_STORAGE_KEY);
  } catch {
    return null;
  }
}

/** Get auth header — uses cached token to avoid Web Lock contention during refresh. */
async function authHeaders(): Promise<Record<string, string>> {
  // Local dev: no auth header needed — backend runs with CHRONA_AUTH_REQUIRED=false.
  // X-Org-Id still injected so local testing can exercise it if desired.
  const orgHeader = readActiveOrgId();
  const xOrg: Record<string, string> = orgHeader ? { 'X-Org-Id': orgHeader } : {};

  if (IS_LOCAL_DEV) return xOrg;

  // Use cached token if available and not expiring within 30s
  const now = Math.floor(Date.now() / 1000);
  if (_cachedToken && _tokenExpiresAt > now + 30) {
    log.log('authHeaders: using cached token');
    return { Authorization: `Bearer ${_cachedToken}`, ...xOrg };
  }

  // Cache miss (first call or token expired) — fall back to getSession() with timeout
  log.log('authHeaders: cache miss, getting supabase session');
  try {
    const result = await Promise.race([
      supabase.auth.getSession(),
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('getSession timeout')), 5000)
      ),
    ]);
    const session = result.data.session;
    log.log('authHeaders: session', session ? 'found' : 'none');
    if (session?.access_token) {
      // Populate cache for subsequent calls
      _cachedToken = session.access_token;
      _tokenExpiresAt = session.expires_at ?? 0;
      return { Authorization: `Bearer ${session.access_token}`, ...xOrg };
    }
  } catch (err) {
    log.warn('authHeaders: getSession failed/timed out, proceeding without auth', err);
  }
  return xOrg;
}

/** Capture slow API responses to PostHog (>500ms only). */
function captureSlowResponse(res: Response, path: string, method: string): void {
  const timing = res.headers.get('X-Response-Time');
  if (!timing) return;
  const ms = parseInt(timing, 10);
  if (ms > 500) {
    Telemetry.apiResponseSlow({
      path,
      method: method || 'GET',
      duration_ms: ms,
      status_code: res.status,
    });
  }
}

/** Error thrown when the server returns 403 (insufficient permissions). */
export class PermissionError extends Error {
  requiredRole: string;
  yourRole: string;

  constructor(message: string, requiredRole = '', yourRole = '') {
    super(message);
    this.name = 'PermissionError';
    this.requiredRole = requiredRole;
    this.yourRole = yourRole;
  }
}

/**
 * Error thrown when the server returns a structured failure carrying an
 * ErrorCode (A2F-1115 / A2F-659). ``code`` matches the backend ``ErrorCode``
 * enum (e.g. ``storage_unconfigured``) so callers can map it to friendly,
 * action-oriented copy; ``message`` is the backend's human-readable text.
 * Non-structured failures still throw a plain ``Error`` from ``apiFetch``.
 */
export class ApiError extends Error {
  status: number;
  code: string | null;

  constructor(message: string, status: number, code: string | null = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

/** Custom event dispatched when an X-Org-Id switch is rejected by SSO policy. */
export const ORG_SWITCH_SSO_REQUIRED_EVENT = 'chrona:sso-required-for-org';

/** Handle 403 responses by throwing a PermissionError with role details.
 *
 *  A2F-244: when the active org switch is rejected (SSO-required or
 *  not-a-member), clear the stored selection so the next request falls
 *  back to the user's default org, and dispatch an event so the UI can
 *  surface a message / offer a Sign-in-with-Microsoft action.
 */
async function handleForbidden(res: Response): Promise<never> {
  let message = "You don't have permission to do this.";
  let requiredRole = '';
  let yourRole = '';
  let code: string | null = null;

  try {
    const body = await res.json();
    const detail = body.detail;
    if (typeof detail === 'object' && detail !== null) {
      message = detail.message || message;
      requiredRole = detail.required_role || '';
      yourRole = detail.your_role || '';
      code = typeof detail.error === 'string' ? detail.error : null;
    } else if (typeof detail === 'string') {
      message = detail;
    }
  } catch {
    // If body isn't JSON, use the default message
  }

  // Only reset when there IS a selected org to reset — otherwise this 403
  // is from a different path (RBAC, deactivation, etc.) and not ours to clear.
  // A2F-990: the message classification (incl. the new "web app access" arm)
  // lives in isOrgSwitchRejectionMessage so it's unit-tested. The hadOrgHeader
  // guard stays here so an org-level web-app-disabled 403 with no stored
  // selection isn't swallowed.
  const hadOrgHeader = readActiveOrgId() !== null;
  const isOrgSwitchRejection =
    hadOrgHeader && isOrgSwitchRejectionMessage(message);
  if (isOrgSwitchRejection) {
    try { localStorage.removeItem(ACTIVE_ORG_STORAGE_KEY); } catch { /* ignore */ }
    try {
      // Always dispatch so ActiveOrgContext resyncs to the home org. The
      // ssoRequired flag tells OrgSelector whether to offer the "Sign in with
      // Microsoft" CTA — only SSO rejections are fixable that way; not-member
      // and no-web-app-access are resolved by the home-org fallback (A2F-990).
      window.dispatchEvent(
        new CustomEvent(ORG_SWITCH_SSO_REQUIRED_EVENT, {
          detail: { message, ssoRequired: isSsoRequiredMessage(message) },
        })
      );
    } catch { /* ignore in non-browser contexts */ }
  }

  // A2F-659 (issue C): a 403 can also be a structured storage/Graph error
  // (e.g. graph_permission_denied) rather than an RBAC denial. Those carry a
  // non-RBAC `detail.error` code and no role fields — re-throw them as
  // ApiError so callers' code-based mapping (friendlyStorageError) fires.
  // insufficient_permissions stays a PermissionError so the role-aware UI
  // (required/your role) keeps working.
  if (code && code !== 'insufficient_permissions') {
    throw new ApiError(message, res.status, code);
  }

  throw new PermissionError(message, requiredRole, yourRole);
}

/**
 * Read a non-ok response and throw the most specific error available.
 *
 * Backend structured errors arrive as ``{detail: {error, message}}`` (the
 * HTTPException envelope) or top-level ``{error, message}`` (commons). Either
 * way we throw an ``ApiError`` carrying the code + message. A non-JSON or
 * unstructured body falls back to the legacy ``API error <status>: <text>``
 * Error so existing callers keep working.
 */
async function throwApiError(res: Response): Promise<never> {
  const text = await res.text();
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new Error(`API error ${res.status}: ${text}`);
  }
  if (parsed && typeof parsed === 'object') {
    const body = parsed as Record<string, unknown>;
    const detail = body.detail;
    const structured =
      detail && typeof detail === 'object'
        ? (detail as Record<string, unknown>)
        : typeof body.error === 'string'
          ? body
          : null;
    if (structured && typeof structured.error === 'string') {
      const message =
        typeof structured.message === 'string'
          ? structured.message
          : `API error ${res.status}`;
      throw new ApiError(message, res.status, structured.error);
    }
  }
  throw new Error(`API error ${res.status}: ${text}`);
}

/** Base fetch wrapper with error handling and snake->camel conversion. */
async function apiFetch<T>(
  path: string,
  options?: RequestInit,
  signal?: AbortSignal
): Promise<T> {
  const url = `${API_BASE}${path}`;
  log.log(`fetch ${options?.method || 'GET'} ${path}`);
  const headers = await authHeaders();
  const res = await fetch(url, {
    ...options,
    signal,
    headers: {
      'Content-Type': 'application/json',
      ...headers,
      ...options?.headers,
    },
  });

  log.log(`fetch ${path} -> ${res.status} (${res.headers.get('X-Response-Time') || '?'}ms)`);
  captureSlowResponse(res, path, options?.method || 'GET');

  if (res.status === 403) {
    await handleForbidden(res);
  }

  if (!res.ok) {
    await throwApiError(res);
  }

  // 204 No Content has no body — return undefined as T so callers using
  // <void> get a sensible value. Calling res.json() on a 204 throws.
  if (res.status === 204) {
    return undefined as T;
  }

  const data = await res.json();
  return convertKeys(data) as T;
}

/** Fetch wrapper for multipart/form-data. */
async function apiUpload<T>(
  path: string,
  formData: FormData,
  signal?: AbortSignal
): Promise<T> {
  const url = `${API_BASE}${path}`;
  const headers = await authHeaders();
  const res = await fetch(url, {
    method: 'POST',
    body: formData,
    signal,
    headers: { ...headers },
  });

  captureSlowResponse(res, path, 'POST');

  if (res.status === 403) {
    await handleForbidden(res);
  }

  if (!res.ok) {
    await throwApiError(res);
  }

  const data = await res.json();
  return convertKeys(data) as T;
}

// --- SharePoint browse shapes (A2F-423) — returned by the /projects/spo/* endpoints ---

export interface SPOSiteInfo {
  id: string;
  displayName: string;
  name: string;
  webUrl: string;
}

export interface SPODriveInfo {
  id: string;
  name: string;
  driveType: string;
  webUrl: string;
  description?: string | null;
}

export interface SPOFolderInfo {
  id: string;
  name: string;
  webUrl: string;
  childCount?: number | null;
  size?: number | null;
}

// --- API methods ---

export const api = {
  /** Expose auth headers for manual fetch calls (e.g. PDF blob download). */
  getAuthHeaders: authHeaders,

  health: () => apiFetch<HealthResponse>('/api/v1/health'),

  projects: {
    list: () => apiFetch<Project[]>('/api/v1/projects'),

    create: (body: {
      name: string;
      ind_number: string;
      root_path?: string;
      spo_drive_id?: string;
      spo_folder_id?: string;
      spo_site_url?: string;
      folder_rules?: { ignore_folder_names: string[]; active_folder_names?: string[]; ignore_seeded?: boolean };
      preferred_file_type?: string;
    }) =>
      apiFetch<Project>('/api/v1/projects', {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    get: (id: string) => apiFetch<Project>(`/api/v1/projects/${id}`),

    // A2F-1035: one read for the folder-rules editor — current user lists,
    // the ignore_seeded marker, the locked system core, the convention
    // defaults, and the fully-composed effective ignore set. Viewer role.
    // Edits still go through update() with a folder_rules body.
    folderFilters: (id: string) =>
      apiFetch<FolderFilterState>(`/api/v1/projects/${id}/folder-filters`),

    // A2F-1035 / FRE: project-agnostic seed constants for the onboarding
    // wizard, which has no project id yet. Returns the convention defaults
    // (proposed editable ignore chips) and the always-on, NON-editable system
    // core. Viewer role. apiFetch camelCases → { defaultIgnoreGlobs, systemCore }.
    folderFilterDefaults: () =>
      apiFetch<FolderFilterDefaults>('/api/v1/projects/folder-filter-defaults'),

    // A2F-423: browse SharePoint via the org's app-only Graph credentials
    // (server-side) instead of the browser's delegated token. This avoids the
    // flaky `/sites?search=` endpoint (500 generalException) and the ephemeral
    // delegated token, and shows exactly what the app principal can read.
    spo: {
      /** Resolve a site — the org's configured site (no arg) or a pasted URL. */
      site: (url?: string) =>
        apiFetch<SPOSiteInfo>(
          `/api/v1/projects/spo/site${url ? `?url=${encodeURIComponent(url)}` : ''}`,
        ),
      /** List a site's document libraries (drives). */
      drives: (siteId: string) =>
        apiFetch<{ drives: SPODriveInfo[] }>(
          `/api/v1/projects/spo/drives?site_id=${encodeURIComponent(siteId)}`,
        ),
      /** List child folders of a drive root, or of `itemId`. */
      folders: (driveId: string, itemId?: string) => {
        const params = new URLSearchParams({ drive_id: driveId });
        if (itemId) params.set('item_id', itemId);
        return apiFetch<{ folders: SPOFolderInfo[] }>(
          `/api/v1/projects/spo/folders?${params.toString()}`,
        );
      },
    },

    // A2F-1024: `includeHidden` is independent of `includeCompleted` and
    // defaults off — only Manage Project opts in. When false (every other
    // caller), the backend hides is_disabled rows and ingest-tombstoned
    // (deleted_at) rows, so the sidebar/Plan/Explorer never see them.
    submissions: (id: string, includeCompleted?: boolean, includeHidden?: boolean) => {
      const params = new URLSearchParams();
      if (includeCompleted) params.set('include_completed', 'true');
      if (includeHidden) params.set('include_hidden', 'true');
      const qs = params.toString();
      return apiFetch<Submission[]>(`/api/v1/projects/${id}/submissions${qs ? `?${qs}` : ''}`);
    },

    reorderSubmissions: (projectId: string, submissionIds: string[]) =>
      apiFetch<{ status: string; count: number }>(
        `/api/v1/projects/${projectId}/submissions/reorder`,
        {
          method: 'PUT',
          body: JSON.stringify({ submission_ids: submissionIds }),
        }
      ),

    // A2F-1024: soft, reversible "keep this folder out" — toggles
    // Submission.is_disabled (sticky user intent). Returns the refreshed
    // SubmissionOut. `lead` role server-side. Submissions are only ever
    // soft-deleted (ingest tombstone) or disabled (user) — there is no
    // hard-delete endpoint. Independent of the ingest-driven deleted_at
    // tombstone.
    disableSubmission: (projectId: string, submissionId: string, disabled: boolean) =>
      apiFetch<Submission>(
        `/api/v1/projects/${projectId}/submissions/${submissionId}/disable`,
        { method: 'PUT', body: JSON.stringify({ disabled }) },
      ),

    updateSubmission: (projectId: string, submissionId: string, body: { target_date?: string | null; start_date?: string | null; is_planning?: boolean }) =>
      apiFetch<Submission>(
        `/api/v1/projects/${projectId}/submissions/${submissionId}`,
        { method: 'PATCH', body: JSON.stringify(body) },
      ),

    setPlanning: (projectId: string, planningIds: string[]) =>
      apiFetch<{ status: string; updated: number }>(
        `/api/v1/projects/${projectId}/submissions/set-planning`,
        { method: 'PUT', body: JSON.stringify({ planning_ids: planningIds }) },
      ),

    completeSubmission: (projectId: string, submissionId: string, body: { comment?: string; metrics_snapshot?: Record<string, number | null> }) =>
      apiFetch<Submission>(
        `/api/v1/projects/${projectId}/submissions/${submissionId}/complete`,
        { method: 'POST', body: JSON.stringify(body) },
      ),

    reopenSubmission: (projectId: string, submissionId: string, body: { comment: string }) =>
      apiFetch<Submission>(
        `/api/v1/projects/${projectId}/submissions/${submissionId}/reopen`,
        { method: 'POST', body: JSON.stringify(body) },
      ),

    overlayStatus: (projectId: string, submissionId: string) =>
      apiFetch<{ hasSupersededFiles: boolean; swappedSections: Record<string, string> }>(
        `/api/v1/projects/${projectId}/submissions/${submissionId}/overlay-status`,
      ),

    documents: (id: string) =>
      apiFetch<Document[]>(`/api/v1/projects/${id}/documents`),

    update: (id: string, body: {
      name?: string;
      ind_number?: string;
      description?: string;
      root_path?: string;
      spo_drive_id?: string;
      spo_folder_id?: string;
      spo_site_url?: string;
      // A2F-1035: ignore_seeded is sticky-on-true server-side — a normal save
      // can't un-seed. The folder-rules editor's "Add recommended" action sets
      // it to true while unioning DEFAULT_IGNORE_GLOBS into ignore_folder_names.
      folder_rules?: { ignore_folder_names: string[]; active_folder_names?: string[]; ignore_seeded?: boolean };
      preferred_file_type?: string;
    }) =>
      apiFetch<Project>(`/api/v1/projects/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),

    delete: (id: string) =>
      apiFetch<{ status: string; id: string }>(`/api/v1/projects/${id}`, {
        method: 'DELETE',
      }),

    ingest: (id: string) =>
      apiFetch<IngestTaskResponse>(`/api/v1/projects/${id}/ingest`, {
        method: 'POST',
      }),

    rescanSubmission: (projectId: string, submissionId: string) =>
      apiFetch<IngestTaskResponse>(
        `/api/v1/projects/${projectId}/submissions/${submissionId}/rescan`,
        { method: 'POST' },
      ),

    scan: (rootPath: string) =>
      apiFetch<ScanResponse>('/api/v1/projects/scan', {
        method: 'POST',
        body: JSON.stringify({ root_path: rootPath }),
      }),

    unclassified: (id: string) =>
      apiFetch<UnclassifiedFile[]>(`/api/v1/projects/${id}/unclassified`),

    classify: (projectId: string, fileId: string, ectdSection: string) =>
      apiFetch<{ status: string; document_id: string; section: string; title: string }>(
        `/api/v1/projects/${projectId}/unclassified/${fileId}/classify`,
        {
          method: 'POST',
          body: JSON.stringify({ ectd_section: ectdSection }),
        }
      ),

    dismissUnclassified: (projectId: string, fileId: string) =>
      apiFetch<{ status: string; id: string }>(
        `/api/v1/projects/${projectId}/unclassified/${fileId}/dismiss`,
        { method: 'POST' }
      ),

    reopenUnclassified: (projectId: string, fileId: string) =>
      apiFetch<{ status: string; id: string }>(
        `/api/v1/projects/${projectId}/unclassified/${fileId}/reopen`,
        { method: 'POST' }
      ),

    duplicates: (projectId: string, includeAcknowledged = false) =>
      apiFetch<DuplicateGroupOut[]>(
        `/api/v1/projects/${projectId}/duplicates?include_acknowledged=${includeAcknowledged}`
      ),

    duplicateCount: (projectId: string) =>
      apiFetch<{ count: number }>(`/api/v1/projects/${projectId}/duplicates/count`),

    resolveDuplicates: (projectId: string, keepIds: string[]) =>
      apiFetch<{ status: string; kept: number; demoted: number }>(
        `/api/v1/projects/${projectId}/duplicates/resolve`,
        {
          method: 'POST',
          body: JSON.stringify({ keep_ids: keepIds }),
        }
      ),

    acknowledgeDuplicates: (projectId: string, submissionId: string, ectdSection: string) =>
      apiFetch<{ status: string; count: number }>(
        `/api/v1/projects/${projectId}/duplicates/acknowledge`,
        {
          method: 'POST',
          body: JSON.stringify({ submission_id: submissionId, ectd_section: ectdSection }),
        }
      ),

    /** A2F-651: bulk-acknowledge every unresolved duplicate group in the project.
     *  Single transaction + single audit event server-side. */
    acknowledgeAllDuplicates: (projectId: string) =>
      apiFetch<{
        status: 'acknowledged' | 'noop';
        groupsAcknowledged: number;
        documentsUpdated: number;
      }>(
        `/api/v1/projects/${projectId}/duplicates/acknowledge-all`,
        { method: 'POST' }
      ),

    unacknowledgeDuplicates: (projectId: string, submissionId: string, ectdSection: string) =>
      apiFetch<{ status: string; count: number }>(
        `/api/v1/projects/${projectId}/duplicates/unacknowledge`,
        {
          method: 'POST',
          body: JSON.stringify({ submission_id: submissionId, ectd_section: ectdSection }),
        }
      ),
  },

  resolve: {
    latest: () => apiFetch<ResolveLatestResponse | null>('/api/v1/resolve/latest'),

    analyze: (request: AnalyzeRequest, signal?: AbortSignal, force?: boolean) => {
      const qs = force ? '?force=true' : '';
      return apiFetch<AnalyzeResponse>(`/api/v1/resolve/analyze${qs}`, {
        method: 'POST',
        body: JSON.stringify({ text: request.text }),
      }, signal);
    },

    upload: (file: File, signal?: AbortSignal, force?: boolean) => {
      const qs = force ? '?force=true' : '';
      const formData = new FormData();
      formData.append('file', file);
      return apiUpload<AnalyzeResponse>(`/api/v1/resolve/analyze/upload${qs}`, formData, signal);
    },
  },

  documents: {
    explorer: (projectId: string) =>
      apiFetch<ExplorerResponse>(`/api/v1/documents/explorer/${projectId}`),

    viewerSections: (projectId: string, submission?: string) => {
      const qs = submission ? `?submission=${encodeURIComponent(submission)}` : '';
      return apiFetch<Array<{
        documentId: string;
        ectdSection: string;
        sectionTitle: string;
        module: string;
        fileName: string;
        fileType: string;
        sequenceNumber: string;
        displaySequence: number;
      }>>(`/api/v1/documents/viewer/${projectId}${qs}`);
    },

    renderFragment: async (documentId: string): Promise<string> => {
      const url = `${API_BASE}/api/v1/documents/${documentId}/render?fragment=true`;
      const headers = await authHeaders();
      const res = await fetch(url, { headers: { ...headers } });
      if (!res.ok) throw new Error(`Render failed: ${res.status}`);
      return res.text();
    },

    info: (documentId: string) =>
      apiFetch<DocumentInfo>(`/api/v1/documents/${documentId}/info`),

    renderUrl: (documentId: string) =>
      `${API_BASE}/api/v1/documents/${documentId}/render`,

    compare: (originalId: string, modifiedId: string) =>
      apiFetch<{
        original: { html: string; text: string };
        modified: { html: string; text: string };
      }>(`/api/v1/documents/compare?original=${originalId}&modified=${modifiedId}`),

    previousVersion: (documentId: string, projectId: string) =>
      apiFetch<{ documentId: string; sequenceNumber: string }>(
        `/api/v1/documents/${documentId}/previous?project_id=${projectId}`,
      ),

    openLocal: async (documentId: string) => {
      const res = await apiFetch<{ status: string; filePath?: string; webUrl?: string }>(
        `/api/v1/documents/${documentId}/open`,
        { method: 'POST' },
      );
      // Default fallback: open SharePoint redirect in new tab.
      // Explorer/Viewer pages override this by handling the return value with
      // their own SSO/modal logic — but fire-and-forget callers (Review pages,
      // Clear page) get a working fallback instead of silently discarding the URL.
      if (res.status === 'redirect' && res.webUrl) {
        window.open(res.webUrl, '_blank');
      }
      return res;
    },
  },

  manifest: {
    status: (projectId: string) =>
      apiFetch<ManifestStatus>(`/api/v1/manifest/status/${projectId}`),

    staleDocuments: (projectId: string) =>
      apiFetch<{ staleDocumentIds: string[] }>(`/api/v1/manifest/stale-documents/${projectId}`),
  },

  tasks: {
    get: (taskId: string) =>
      apiFetch<TaskStatus>(`/api/v1/tasks/${taskId}`),

    findRunning: (projectId: string, taskType: string) =>
      apiFetch<TaskStatus>(`/api/v1/tasks/running/${projectId}/${taskType}`),

    cancel: (taskId: string) =>
      apiFetch<{ status: string }>(`/api/v1/tasks/${taskId}/cancel`, { method: 'POST' }),
  },

  renameConsistency: {
    // A2F-815: read via api.consistency.get() and pull
    // per_feature.rename_consistency_inconsistent.value
    // (RenameConsistencyScanResponse). No GET here.
    //
    // ``submissionId`` is the focused sub (NOT an overlay marker —
    // PR #404 review fixed this conflation). Required for a non-empty
    // result; without it the scan returns empty.
    scan: (projectId: string, submissionId?: string) =>
      apiFetch<TaskStartResponse>(
        `/api/v1/projects/${projectId}/rename_consistency/scan${submissionId ? `?submission_id=${submissionId}` : ''}`,
        { method: 'POST' },
      ),

    clearCache: (projectId: string) =>
      apiFetch<{ status: string; deleted: number }>(
        `/api/v1/projects/${projectId}/rename_consistency/cache`,
        { method: 'DELETE' },
      ),

    // A2F-874/875: cluster-level dismissal keyed on the source identifier.
    dismiss: (projectId: string, sourceId: string, reason = '') =>
      apiFetch<{ status: string; source_id: string }>(
        `/api/v1/projects/${projectId}/rename_consistency/dismiss`,
        {
          method: 'POST',
          body: JSON.stringify({ source_id: sourceId, reason }),
        },
      ),

    undismiss: (projectId: string, sourceId: string) =>
      apiFetch<{ status: string; source_id: string }>(
        `/api/v1/projects/${projectId}/rename_consistency/undismiss`,
        {
          method: 'POST',
          body: JSON.stringify({ source_id: sourceId }),
        },
      ),

    // A2F-898: per-instance (single stale-location) dismissal.
    dismissInstance: (
      projectId: string,
      inst: { sourceId: string; documentId: string; sectionPath: string; contextHash: string; occurrence: number },
      reason = '',
    ) =>
      apiFetch<{ status: string; source_id: string; context_hash: string }>(
        `/api/v1/projects/${projectId}/rename_consistency/dismiss_instance`,
        {
          method: 'POST',
          body: JSON.stringify({
            source_id: inst.sourceId,
            document_id: inst.documentId,
            section_path: inst.sectionPath,
            context_hash: inst.contextHash,
            occurrence: inst.occurrence,
            reason,
          }),
        },
      ),

    undismissInstance: (
      projectId: string,
      inst: { sourceId: string; documentId: string; sectionPath: string; contextHash: string; occurrence: number },
    ) =>
      apiFetch<{ status: string; source_id: string; context_hash: string }>(
        `/api/v1/projects/${projectId}/rename_consistency/undismiss_instance`,
        {
          method: 'POST',
          body: JSON.stringify({
            source_id: inst.sourceId,
            document_id: inst.documentId,
            section_path: inst.sectionPath,
            context_hash: inst.contextHash,
            occurrence: inst.occurrence,
          }),
        },
      ),
  },

  abbreviations: {
    // A2F-462: `.get()` removed — read via api.consistency.get() and pull
    // per_feature.abbreviations_inconsistent.value (same shape as before).
    scan: (projectId: string, overlayId?: string) =>
      apiFetch<TaskStartResponse>(
        `/api/v1/projects/${projectId}/abbreviations/scan${overlayId ? `?submission_overlay_id=${overlayId}` : ''}`,
        { method: 'POST' },
      ),

    clearCache: (projectId: string) =>
      apiFetch<{ status: string; deleted: number }>(
        `/api/v1/projects/${projectId}/abbreviations/cache`,
        { method: 'DELETE' },
      ),

    dismiss: (projectId: string, abbreviation: string, reason = '') =>
      apiFetch<{ status: string; abbreviation: string }>(
        `/api/v1/projects/${projectId}/abbreviations/dismiss`,
        {
          method: 'POST',
          body: JSON.stringify({ abbreviation, reason }),
        },
      ),

    undismiss: (projectId: string, abbreviation: string) =>
      apiFetch<{ status: string; abbreviation: string }>(
        `/api/v1/projects/${projectId}/abbreviations/undismiss`,
        {
          method: 'POST',
          body: JSON.stringify({ abbreviation }),
        },
      ),
  },

  compoundCode: {
    // A2F-1176: Compound Code is no longer a standalone Review feature. The
    // scan still runs (Scan All / Planning) to feed the normalization-collision
    // finding, the Abbreviations prefix filter, and project metadata — but the
    // standalone page (and its clearCache/dismiss/undismiss calls) was removed.
    // Dismissal UX for the collision finding is re-homed in a follow-up.
    // A2F-462: `.get()` removed — read via api.consistency.get().
    scan: (projectId: string, signal?: AbortSignal, fuzzy = true, overlayId?: string) =>
      apiFetch<CompoundCodeScanResponse>(
        `/api/v1/projects/${projectId}/compound-code/scan?fuzzy=${fuzzy}${overlayId ? `&submission_overlay_id=${overlayId}` : ''}`,
        { method: 'POST' },
        signal,
      ),
  },

  sponsorConsistency: {
    // A2F-1158: standalone (not in the consistency aggregate yet).
    // Read path — viewers can load the cached result without triggering a
    // scan/write (review H1/H2). Returns null when not yet scanned.
    get: (projectId: string, overlayId?: string) =>
      apiFetch<SponsorConsistencyScanResponse | null>(
        `/api/v1/projects/${projectId}/sponsor-consistency${overlayId ? `?submission_overlay_id=${overlayId}` : ''}`,
      ),

    // The scan is deterministic and returns the full result synchronously — no
    // task poll. Contributor-gated (writes the cache).
    scan: (projectId: string, signal?: AbortSignal, overlayId?: string) =>
      apiFetch<SponsorConsistencyScanResponse>(
        `/api/v1/projects/${projectId}/sponsor-consistency/scan${overlayId ? `?submission_overlay_id=${overlayId}` : ''}`,
        { method: 'POST' },
        signal,
      ),

    clearCache: (projectId: string) =>
      apiFetch<{ status: string; deleted: number }>(
        `/api/v1/projects/${projectId}/sponsor-consistency/cache`,
        { method: 'DELETE' },
      ),

    dismiss: (projectId: string, findingKey: string, reason = '') =>
      apiFetch<{ status: string; findingKey: string }>(
        `/api/v1/projects/${projectId}/sponsor-consistency/dismiss`,
        {
          method: 'POST',
          body: JSON.stringify({ finding_key: findingKey, reason }),
        },
      ),

    undismiss: (projectId: string, findingKey: string) =>
      apiFetch<{ status: string; findingKey: string }>(
        `/api/v1/projects/${projectId}/sponsor-consistency/undismiss`,
        {
          method: 'POST',
          body: JSON.stringify({ finding_key: findingKey }),
        },
      ),
  },

  // A2F-1366: Study Profile — per-study assembly over the reconciliation engine
  // (dev-gated surface, A2F-1361). Worker-backed like reconciliation: GET reads the
  // cached assembly, POST /scan enqueues the study_profile_scan job, dismiss/undismiss
  // hit the SHARED finding store (one finding, one dismissal across surfaces).
  studyProfile: {
    get: (projectId: string, overlayId?: string) =>
      apiFetch<StudyProfileResponse | null>(
        `/api/v1/projects/${projectId}/study-profile${overlayId ? `?submission_overlay_id=${overlayId}` : ''}`,
      ),

    scan: (projectId: string, signal?: AbortSignal, overlayId?: string, force?: boolean) => {
      const params = new URLSearchParams();
      if (overlayId) params.set('submission_overlay_id', overlayId);
      if (force) params.set('force', 'true');
      const qs = params.toString();
      return apiFetch<{ taskId: string; resumed: boolean }>(
        `/api/v1/projects/${projectId}/study-profile/scan${qs ? `?${qs}` : ''}`,
        { method: 'POST' },
        signal,
      );
    },

    dismiss: (projectId: string, findingKey: string, reason: string) =>
      apiFetch<{ status: string; finding_key: string }>(
        `/api/v1/projects/${projectId}/study-profile/dismiss`,
        { method: 'POST', body: JSON.stringify({ finding_key: findingKey, reason }) },
      ),

    undismiss: (projectId: string, findingKey: string) =>
      apiFetch<{ status: string; finding_key: string }>(
        `/api/v1/projects/${projectId}/study-profile/undismiss`,
        { method: 'POST', body: JSON.stringify({ finding_key: findingKey }) },
      ),
  },

  // A2F-1363/A2F-1361: Substance Profile — per-substance assembly (dev-gated). Same
  // worker-backed pattern as study-profile: GET reads the cache, POST /scan enqueues the
  // substance_profile_scan job, dismiss/undismiss hit the SHARED finding store.
  substanceProfile: {
    get: (projectId: string, overlayId?: string) =>
      apiFetch<SubstanceProfileResponse | null>(
        `/api/v1/projects/${projectId}/substance-profile${overlayId ? `?submission_overlay_id=${overlayId}` : ''}`,
      ),

    scan: (projectId: string, signal?: AbortSignal, overlayId?: string, force?: boolean) => {
      const params = new URLSearchParams();
      if (overlayId) params.set('submission_overlay_id', overlayId);
      if (force) params.set('force', 'true');
      const qs = params.toString();
      return apiFetch<{ taskId: string; resumed: boolean }>(
        `/api/v1/projects/${projectId}/substance-profile/scan${qs ? `?${qs}` : ''}`,
        { method: 'POST' },
        signal,
      );
    },

    dismiss: (projectId: string, findingKey: string, reason: string) =>
      apiFetch<{ status: string; finding_key: string }>(
        `/api/v1/projects/${projectId}/substance-profile/dismiss`,
        { method: 'POST', body: JSON.stringify({ finding_key: findingKey, reason }) },
      ),

    undismiss: (projectId: string, findingKey: string) =>
      apiFetch<{ status: string; finding_key: string }>(
        `/api/v1/projects/${projectId}/substance-profile/undismiss`,
        { method: 'POST', body: JSON.stringify({ finding_key: findingKey }) },
      ),
  },

  crossReferences: {
    // A2F-462: `.get()` removed — read via api.consistency.get().
    scan: (projectId: string, signal?: AbortSignal, overlayId?: string) =>
      apiFetch<CrossReferenceScanResponse>(
        `/api/v1/projects/${projectId}/cross-references/scan${overlayId ? `?submission_overlay_id=${overlayId}` : ''}`,
        { method: 'POST' },
        signal,
      ),

    clearCache: (projectId: string) =>
      apiFetch<{ status: string; deleted: number }>(
        `/api/v1/projects/${projectId}/cross-references/cache`,
        { method: 'DELETE' },
      ),

    dismiss: (projectId: string, sectionNumber: string, reason = '') =>
      apiFetch<{ status: string; sectionNumber: string }>(
        `/api/v1/projects/${projectId}/cross-references/dismiss`,
        {
          method: 'POST',
          body: JSON.stringify({ section_number: sectionNumber, reason }),
        },
      ),

    undismiss: (projectId: string, sectionNumber: string) =>
      apiFetch<{ status: string; sectionNumber: string }>(
        `/api/v1/projects/${projectId}/cross-references/undismiss`,
        {
          method: 'POST',
          body: JSON.stringify({ section_number: sectionNumber }),
        },
      ),
  },

  doseUnit: {
    // A2F-462: `.get()` removed — read via api.consistency.get().
    scan: (projectId: string, overlayId?: string) =>
      apiFetch<TaskStartResponse>(
        `/api/v1/projects/${projectId}/dose-unit/scan${overlayId ? `?submission_overlay_id=${overlayId}` : ''}`,
        { method: 'POST' },
      ),

    clearCache: (projectId: string) =>
      apiFetch<{ status: string; deleted: number }>(
        `/api/v1/projects/${projectId}/dose-unit/cache`,
        { method: 'DELETE' },
      ),

    dismiss: (projectId: string, substanceName: string, reason = '', dosageKey?: string) =>
      apiFetch<{ status: string; substanceName: string; dosageKey: string | null }>(
        `/api/v1/projects/${projectId}/dose-unit/dismiss`,
        {
          method: 'POST',
          body: JSON.stringify({
            substance_name: substanceName,
            reason,
            ...(dosageKey !== undefined ? { dosage_key: dosageKey } : {}),
          }),
        },
      ),

    undismiss: (projectId: string, substanceName: string, dosageKey?: string) =>
      apiFetch<{ status: string; substanceName: string; dosageKey: string | null }>(
        `/api/v1/projects/${projectId}/dose-unit/undismiss`,
        {
          method: 'POST',
          body: JSON.stringify({
            substance_name: substanceName,
            ...(dosageKey !== undefined ? { dosage_key: dosageKey } : {}),
          }),
        },
      ),
  },

  sectionOwners: {
    get: (projectId: string, submissionId?: string) => {
      const qs = submissionId ? `?submission_id=${submissionId}` : '';
      return apiFetch<SectionOwnerScanResponse>(`/api/v1/projects/${projectId}/section-owners${qs}`);
    },

    scan: (projectId: string, signal?: AbortSignal, submissionId?: string) => {
      const qs = submissionId ? `?submission_id=${submissionId}` : '';
      return apiFetch<SectionOwnerScanResponse>(
        `/api/v1/projects/${projectId}/section-owners/scan${qs}`,
        { method: 'POST' },
        signal,
      );
    },

    clearCache: (projectId: string, submissionId?: string) => {
      const qs = submissionId ? `?submission_id=${submissionId}` : '';
      return apiFetch<{ status: string; deleted: number }>(
        `/api/v1/projects/${projectId}/section-owners/cache${qs}`,
        { method: 'DELETE' },
      );
    },

    dismiss: (projectId: string, ownerName: string, reason = '') =>
      apiFetch<{ status: string; ownerName: string }>(
        `/api/v1/projects/${projectId}/section-owners/dismiss`,
        {
          method: 'POST',
          body: JSON.stringify({ owner_name: ownerName, reason }),
        },
      ),

    undismiss: (projectId: string, ownerName: string) =>
      apiFetch<{ status: string; ownerName: string }>(
        `/api/v1/projects/${projectId}/section-owners/undismiss`,
        {
          method: 'POST',
          body: JSON.stringify({ owner_name: ownerName }),
        },
      ),
  },

  documentReadiness: {
    /** A2F-663 — document-level sign-off persistence. Per-submission CRUD. */
    getAssignments: (submissionId: string) =>
      apiFetch<{
        assignments: Record<
          string,
          Array<{
            userId: string;
            state: 'pending' | 'signed';
            signedAt: string | null;
            comment: string | null;
          }>
        >;
      }>(`/api/v1/document-readiness/${submissionId}/assignments`),

    setOwners: (submissionId: string, documentId: string, userIds: string[]) =>
      apiFetch<{
        documentId: string;
        owners: Array<{
          userId: string;
          state: 'pending' | 'signed';
          signedAt: string | null;
          comment: string | null;
        }>;
      }>(`/api/v1/document-readiness/${submissionId}/owners`, {
        method: 'POST',
        body: JSON.stringify({ document_id: documentId, user_ids: userIds }),
      }),

    sign: (submissionId: string, documentId: string, comment?: string | null) =>
      apiFetch<{
        documentId: string;
        owner: {
          userId: string;
          state: 'pending' | 'signed';
          signedAt: string | null;
          comment: string | null;
        };
      }>(`/api/v1/document-readiness/${submissionId}/sign`, {
        method: 'POST',
        body: JSON.stringify({ document_id: documentId, comment: comment ?? null }),
      }),

    // A2F-758 MAINTAINABILITY GUARD: `revoke` is currently uncalled — there is
    // no document-level "un-sign" UI. If one is ever added (e.g. a revoke
    // button wired to this endpoint), the caller MUST also fire
    // `invalidateDocumentReadiness(projectId)` so the Gantt stays in sync.
    // Failure to do so will silently leave the Gantt showing stale metrics.
    revoke: (submissionId: string, documentId: string) =>
      apiFetch<{
        documentId: string;
        owner: {
          userId: string;
          state: 'pending' | 'signed';
          signedAt: string | null;
          comment: string | null;
        };
      }>(
        `/api/v1/document-readiness/${submissionId}/sign?document_id=${encodeURIComponent(documentId)}`,
        { method: 'DELETE' },
      ),
  },

  consistency: {
    /** A2F-462 follow-up: single public GET path for consistency reads.
     *  Server owns the overlay-vs-lifecycle merge.
     *  summary=true omits per_feature — use for Plan Gantt lanes.
     *  A2F-1176 follow-up: the fuzzy selector was dropped — Compound Code no
     *  longer participates in the consistency aggregate, so the server ignores
     *  a `fuzzy` query param. (The CompoundCode scan itself keeps its own
     *  fuzzy param on api.compoundCode.scan.) */
    get: (projectId: string, submissionId?: string, summary = false) => {
      const params = new URLSearchParams();
      if (submissionId) params.set('submission_id', submissionId);
      if (summary) params.set('summary', 'true');
      const qs = params.toString();
      return apiFetch<ConsistencyResponse>(
        `/api/v1/projects/${projectId}/consistency${qs ? `?${qs}` : ''}`,
      );
    },
  },

  readiness: {
    declare: (
      projectId: string,
      snapshotPayload: Record<string, unknown>,
      declarationText?: string,
      autoAssemble?: boolean,
    ) =>
      apiFetch<ReadinessSnapshot>(
        `/api/v1/projects/${projectId}/readiness/declare`,
        {
          method: 'POST',
          body: JSON.stringify({
            snapshot_payload: snapshotPayload,
            declaration_text: declarationText,
            auto_assemble: autoAssemble ?? false,
          }),
        },
      ),

    list: (projectId: string, includeSuperseded = false) =>
      apiFetch<ReadinessSnapshot[]>(
        `/api/v1/projects/${projectId}/readiness?include_superseded=${includeSuperseded}`,
      ),

    declarationText: (projectId: string) =>
      apiFetch<{ declarationText: string }>(`/api/v1/projects/${projectId}/readiness/declaration-text`),
  },

  audit: {
    events: (entityType: string, entityId: string) =>
      apiFetch<AuditEvent[]>(`/api/v1/audit/events/${entityType}/${entityId}`),

    list: (params?: { eventType?: string; entityType?: string; limit?: number; offset?: number }) => {
      const qs = new URLSearchParams();
      if (params?.eventType) qs.set('event_type', params.eventType);
      if (params?.entityType) qs.set('entity_type', params.entityType);
      if (params?.limit) qs.set('limit', String(params.limit));
      if (params?.offset) qs.set('offset', String(params.offset));
      const q = qs.toString();
      return apiFetch<AuditEvent[]>(`/api/v1/audit/events${q ? `?${q}` : ''}`);
    },
  },

  settings: {
    get: () => apiFetch<AppConfig>('/api/v1/settings'),

    update: (body: {
      llm_provider?: string;
      llm_model?: string;
      anthropic_api_key?: string;
      openai_api_key?: string;
      /** A2F-1019: IANA timezone string; validated on the backend (400 on bad TZ). */
      display_timezone?: string;
    }) =>
      apiFetch<AppConfig>('/api/v1/settings', {
        method: 'PUT',
        body: JSON.stringify(body),
      }),

    models: (provider: string) =>
      apiFetch<ModelOption[]>(`/api/v1/settings/models?provider=${encodeURIComponent(provider)}`),
  },

  auth: {
    setupStatus: () => apiFetch<SetupStatusResponse>('/api/v1/auth/setup-status'),

    me: () => apiFetch<AuthUser | null>('/api/v1/auth/me'),

    checkDomain: (email: string) =>
      apiFetch<CheckDomainResponse>(`/api/v1/auth/check-domain?email=${encodeURIComponent(email)}`),
  },

  me: {
    account: () => apiFetch<AccountResponse>('/api/v1/me/account'),
  },

  submissionTypes: {
    listAll: () =>
      apiFetch<SubmissionTypeOption[]>('/api/v1/submission-types'),

    getClassification: (projectId: string, submissionId: string) =>
      apiFetch<SubmissionClassification>(
        `/api/v1/projects/${projectId}/submissions/${submissionId}/classification`,
      ),

    inferType: (projectId: string, submissionId: string) =>
      apiFetch<{ inferred: { code: string; label: string; confidence: number; matchedSections: number } | null }>(
        `/api/v1/projects/${projectId}/submissions/${submissionId}/classification/infer`,
      ),

    setClassification: (projectId: string, submissionId: string, typeCode: string) =>
      apiFetch<SubmissionClassification>(
        `/api/v1/projects/${projectId}/submissions/${submissionId}/classification`,
        {
          method: 'PUT',
          body: JSON.stringify({ type_code: typeCode }),
        },
      ),

    confirm: (projectId: string, submissionId: string) =>
      apiFetch<{ status: string; submissionId: string; state: string }>(
        `/api/v1/projects/${projectId}/submissions/${submissionId}/classification/confirm`,
        { method: 'POST' },
      ),

    reset: (projectId: string, submissionId: string) =>
      apiFetch<{ status: string; submissionId: string }>(
        `/api/v1/projects/${projectId}/submissions/${submissionId}/classification/reset`,
        { method: 'POST' },
      ),

    checkPresence: (projectId: string, submissionId: string) =>
      apiFetch<SubmissionClassification>(
        `/api/v1/projects/${projectId}/submissions/${submissionId}/classification/check-presence`,
        { method: 'POST' },
      ),

    dismiss: (projectId: string, submissionId: string, suggestionId: string, reason: string) =>
      apiFetch<{ status: string; id: string }>(
        `/api/v1/projects/${projectId}/submissions/${submissionId}/suggestions/${suggestionId}/dismiss`,
        {
          method: 'POST',
          body: JSON.stringify({ reason }),
        },
      ),

    undismiss: (projectId: string, submissionId: string, suggestionId: string) =>
      apiFetch<{ status: string; id: string }>(
        `/api/v1/projects/${projectId}/submissions/${submissionId}/suggestions/${suggestionId}/undismiss`,
        { method: 'POST' },
      ),

    analyzeLetter: (projectId: string, submissionId: string, file: File) =>
      apiUpload<SubmissionClassification>(
        `/api/v1/projects/${projectId}/submissions/${submissionId}/classification/analyze-letter`,
        (() => { const fd = new FormData(); fd.append('file', file); return fd; })(),
      ),

    clearAnalysis: (projectId: string, submissionId: string) =>
      apiFetch<SubmissionClassification>(
        `/api/v1/projects/${projectId}/submissions/${submissionId}/classification/clear-analysis`,
        { method: 'POST' },
      ),

    addSection: (projectId: string, submissionId: string, sectionNumber: string, sectionTitle: string, module: string) =>
      apiFetch<SubmissionClassification>(
        `/api/v1/projects/${projectId}/submissions/${submissionId}/classification/add-section`,
        { method: 'POST', body: JSON.stringify({ section_number: sectionNumber, section_title: sectionTitle, module }) },
      ),

    // A2F-565: bulk-accept every currently-observed unexpected section in one gesture.
    // Backend returns the refreshed classification on success, or 409 with
    // {error, message, drifted_sections} when the observed set has changed
    // between the client's fetch and this request.
    acceptUnexpected: (projectId: string, submissionId: string, sections: string[]) =>
      apiFetch<SubmissionClassification>(
        `/api/v1/projects/${projectId}/submissions/${submissionId}/sections/accept-unexpected`,
        { method: 'POST', body: JSON.stringify({ sections }) },
      ),
  },

  orgs: {
    invite: (orgId: string, email: string, firstName: string, lastName: string, role: string) =>
      apiFetch<InviteResponse>(`/api/v1/orgs/${orgId}/invite`, {
        method: 'POST',
        body: JSON.stringify({ email, first_name: firstName, last_name: lastName, role }),
      }),

    members: (orgId: string) =>
      apiFetch<OrgMember[]>(`/api/v1/orgs/${orgId}/members`),

    changeRole: (orgId: string, email: string, role: string) =>
      apiFetch<{ status: string; email: string; role: string }>(
        `/api/v1/orgs/${orgId}/members/${encodeURIComponent(email)}/role`,
        { method: 'PUT', body: JSON.stringify({ role }) },
      ),

    // A2F-699: rationale-required POST (replaces legacy PUT /status).
    // Server enforces ≥10-char rationale via RationaleRequest.
    disable: (orgId: string, email: string, rationale: string) =>
      apiFetch<{ status: string; email: string }>(
        `/api/v1/orgs/${orgId}/members/${encodeURIComponent(email)}/disable`,
        { method: 'POST', body: JSON.stringify({ rationale }) },
      ),

    // A2F-699: rationale-required POST (replaces legacy PUT /status).
    enable: (orgId: string, email: string, rationale: string) =>
      apiFetch<{ status: string; email: string }>(
        `/api/v1/orgs/${orgId}/members/${encodeURIComponent(email)}/enable`,
        { method: 'POST', body: JSON.stringify({ rationale }) },
      ),

    resendInvite: (orgId: string, email: string) =>
      apiFetch<InviteResponse>(
        `/api/v1/orgs/${orgId}/members/${encodeURIComponent(email)}/resend-invite`,
        { method: 'POST' },
      ),

    // A2F-583 / A2F-699: hard-delete a pending invitation, now via POST
    // with a Part 11 rationale in the body. Returns 204 on success; a 409
    // means the invitee accepted between page load and click — caller
    // should reload the member list and switch to the Disable flow.
    revokeInvite: (orgId: string, email: string, rationale: string) =>
      apiFetch<void>(
        `/api/v1/orgs/${orgId}/members/${encodeURIComponent(email)}/invite/revoke`,
        { method: 'POST', body: JSON.stringify({ rationale }) },
      ),

    // A2F-698: bulk admin actions. One rationale, N targets, single batch_id
    // shared across the resulting audit_events rows. Per-row failures land in
    // `failures: [{email, reason}]` without blocking subsequent rows.
    bulkDisable: (orgId: string, emails: string[], rationale: string) =>
      apiFetch<{ batchId: string; successes: string[]; failures: { email: string; reason: string }[] }>(
        `/api/v1/orgs/${orgId}/members/bulk-disable`,
        { method: 'POST', body: JSON.stringify({ emails, rationale }) },
      ),

    bulkRevokeInvites: (orgId: string, emails: string[], rationale: string) =>
      apiFetch<{ batchId: string; successes: string[]; failures: { email: string; reason: string }[] }>(
        `/api/v1/orgs/${orgId}/members/bulk-revoke-invites`,
        { method: 'POST', body: JSON.stringify({ emails, rationale }) },
      ),
  },
};
